import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import { CheckCircle2, Loader2 } from 'lucide-react';
import * as Yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage';
import { ResultCard } from '../../components/ResultCard';
import { Badge, Button, Card, Checkbox } from '../../components/primitives';
import { OnboardingLayout } from '../OnboardingLayout';
import { GET_TERMS_AND_CONDITIONS_QUERY } from './queries';
import { TermsAndConditions } from '@shopify-multiseller/graphql/partner/types';
import { formatDate } from '../../helpers/formatters';
import { TermsDocument } from './TermsDocument';

interface AcceptTermsAndConditionsProps {
    updated?: boolean;
}

interface AcceptTermsValues {
    terms: boolean;
}

const AcceptTermsAndConditions: React.FC<AcceptTermsAndConditionsProps> = ({ updated }) => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [getTermsAndConditions] = useLazyQuery<
        { termsAndConditions: TermsAndConditions },
        {}
    >(GET_TERMS_AND_CONDITIONS_QUERY);
    const [success, setSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [termsAndConditions, setTermsAndConditions] = useState<TermsAndConditions | undefined>(undefined);
    const [error] = useState<Error | undefined>(undefined);
    // How far down the agreement the reader has got. Informative, not a gate:
    // the accept button never waits on it. It exists so a document in a
    // 26rem box says how much of itself is left, which a scrollbar in an
    // iframe does not do convincingly.
    const [progress, setProgress] = useState<{ read: number; total: number } | undefined>(undefined);

    useEffect(() => {
        if (auth?.isLoggedIn && success) {
            setTimeout(() => {
                navigate('/');
            }, 1000);
        }
    }, [auth, navigate, success]);

    useEffect(() => {
        getTermsAndConditions().then((result) => {
            setTermsAndConditions(result.data?.termsAndConditions);
            setLoading(false);
        }).catch((error) => {
            console.error('Failed to load terms and conditions', error);
        });
    }, []);

    const handleOnSubmit = async (values: AcceptTermsValues, actions: FormikHelpers<AcceptTermsValues>) => {
        if (!termsAndConditions) return;
        await auth?.acceptTerms(termsAndConditions.version)
            .then(() => {
                setSuccess(true);
            })
            .catch((error) => {
                actions.setSubmitting(false);
                console.error('Failed to accept terms and conditions', error);
            });
    };

    if (error || (!loading && !termsAndConditions)) return <ErrorPage title="The seller agreement didn't load" message="The connection dropped or the server is busy. Nothing has been agreed to yet." link={{ label: "Try again", url: "/" }} />;

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
                <ResultCard
                    tone="success"
                    icon={CheckCircle2}
                    title="User Agreement Accepted"
                    description="Redirecting you to your dashboard."
                    testId="terms-accepted"
                />
            </div>
        );
    }

    // "You agreed to version 3.1 in March." — from the seller's own record,
    // which carries both the version they accepted and when.
    const accepted = auth?.user?.seller?.terms;
    const previousAgreement =
        accepted?.version && accepted?.acceptedAt
            ? `You agreed to version ${accepted.version} in ${formatDate(accepted.acceptedAt, { month: 'long' })}.`
            : undefined;

    const documentPanel = (
        <Card className="overflow-hidden">
            {/* Its own scroll, so the accept controls stay put while the
                agreement is read — previously the whole page scrolled and the
                checkbox went with it. */}
            <TermsDocument
                content={termsAndConditions?.content}
                loading={loading}
                onProgress={setProgress}
                className="h-[26rem] overflow-auto"
            />
            <Formik
                initialValues={{ terms: false }}
                onSubmit={handleOnSubmit}
                validationSchema={Yup.object().shape({
                    terms: Yup.boolean().oneOf([true], 'Please accept the terms and conditions to continue'),
                })}
            >
                {(props: FormikProps<AcceptTermsValues>) => (
                    <Form className="flex flex-wrap items-center gap-3 border-t border-border bg-muted/40 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="terms"
                                data-testid="terms-checkbox"
                                required
                                checked={props.values.terms}
                                onCheckedChange={(value) => props.setFieldValue('terms', value === true)}
                                onBlur={() => props.setFieldTouched('terms', true)}
                                disabled={props.isSubmitting || loading}
                            />
                            <label htmlFor="terms" className="cursor-pointer select-none text-sm text-foreground">
                                {/* "seller agreement", matching the heading above it and the
                                    design. The page called the document one thing and the
                                    consent checkbox another, which is a poor look on the one
                                    control that records agreement. */}
                                {'I\u2019ve read and agree to the seller agreement'}
                            </label>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                            {/* Quiet, and it says what it does — rejecting signs
                                you out, which "Reject User Agreement" did not. */}
                            <Button type="button" variant="ghost" size="sm" onClick={() => auth?.logout()}>
                                Decline &amp; sign out
                            </Button>
                            <Button
                                type="submit"
                                disabled={props.isSubmitting || !props.values.terms || loading}
                                data-testid="terms-accept-button"
                            >
                                {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {updated ? 'Agree & continue' : 'Agree & open dashboard'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Card>
    );

    // A returning seller whose terms changed is being interrupted, not
    // onboarded — no stepper, and the complete agreement to re-read.
    if (updated) {
        return (
            <div className="flex min-h-screen items-start justify-center bg-surface px-4 py-12">
                {/* Narrower than the first-time screen, as drawn: this one is
                    an interruption to read and clear, not a document to
                    settle into. */}
                <div className="w-full max-w-[35rem]">
                    <Badge variant="primary">Terms updated</Badge>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
                        {termsAndConditions?.createdAt
                            ? `The seller agreement changed on ${formatDate(termsAndConditions.createdAt, { month: 'long', day: 'numeric' })}`
                            : 'The seller agreement has changed'}
                    </h2>
                    {/* How long this will take, and what it replaces. Being
                        stopped at the door by a legal document is worse when
                        nothing says whether it is a minute or an afternoon,
                        and a seller who cannot see which version they last
                        agreed to has no way to judge what changed. */}
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {previousAgreement
                            ? `Takes about a minute. ${previousAgreement}`
                            : 'Takes about a minute. Please read the agreement and accept it to continue.'}
                    </p>
                    <div className="mt-6">{documentPanel}</div>
                </div>
            </div>
        );
    }

    return (
        <OnboardingLayout
            step="terms"
            width="document"
            title="Seller agreement"
            subtitle={
                // Version and when it was issued, as the design states it. "the
                // last step" belonged here only because the rail did not say it;
                // the rail's Terms step carries "Last step" now, so this line can
                // do its actual job — telling the seller which agreement this is.
                termsAndConditions?.version
                    ? `Version ${termsAndConditions.version}${
                          termsAndConditions.createdAt
                              ? ` · updated ${formatDate(termsAndConditions.createdAt, {
                                    month: 'long',
                                    year: 'numeric',
                                })}`
                              : ''
                      }`
                    : 'The last step'
            }
        >
            {documentPanel}
            {progress && progress.total > 1 && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="terms-scroll-progress">
                    {`Scrolled ${progress.read} of ${progress.total} sections`}
                </p>
            )}
        </OnboardingLayout>
    );
};

export default AcceptTermsAndConditions;
